import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import SEO from '@/components/feature/SEO';
import { OrganizationSchema, WebsiteSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" description="抱歉，您要查找的文章不存在。" noIndex />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={`高义白洁,${article.categoryName},${article.title},君子品格,传统美德`}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <WebPageSchema
        pageUrl={`/article/${article.slug}`}
        pageTitle={article.title}
        pageDescription={article.excerpt}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: '/' },
          { name: article.categoryName, item: `/category/${article.category}` },
          { name: article.title },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          ...articleSchema,
        }, null, 2) }}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="w-full py-8 md:py-12">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <header className="mb-8">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-4 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {article.categoryName}
                </Link>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读 {article.readTime}
                  </span>
                </div>
              </header>

              <div className="relative w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="article-content prose max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <hr className="my-10 border-background-200" />

              {relatedArticles.length > 0 && (
                <section>
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                    相关文章
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.id} {...ra} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}